import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from "@/components/ui/command";
import { api } from "@/lib/api";
import {
  LayoutDashboard, Target, TrendingUp, FileText, Receipt, LifeBuoy, Users, Zap, Bot, BarChart3,
  Plus, Search as SearchIcon, UserRound, Building2,
} from "lucide-react";

const NAV_ACTIONS = [
  { label: "Go to Dashboard", icon: LayoutDashboard, to: "/app" },
  { label: "Open CRM / Leads", icon: Target, to: "/app/crm" },
  { label: "Open Sales Pipeline", icon: TrendingUp, to: "/app/sales" },
  { label: "Open Quotations", icon: FileText, to: "/app/quotations" },
  { label: "Open Billing / Invoices", icon: Receipt, to: "/app/billing" },
  { label: "Open Support", icon: LifeBuoy, to: "/app/support" },
  { label: "Open Team Live", icon: Users, to: "/app/team" },
  { label: "Open War Room", icon: Zap, to: "/app/war-room" },
  { label: "Open AI Assistant", icon: Bot, to: "/app/ai" },
  { label: "Open Analytics", icon: BarChart3, to: "/app/analytics" },
];

const TYPE_ROUTE = { lead: "/app/crm", opportunity: "/app/sales", invoice: "/app/billing", ticket: "/app/support" };
const TYPE_ICON = { lead: UserRound, opportunity: TrendingUp, invoice: Receipt, ticket: LifeBuoy };

export function CommandPalette({ open, setOpen }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search`, { params: { q: query } });
        setResults(data);
      } catch {
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const go = (to) => {
    setOpen(false);
    setQuery("");
    navigate(to);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Search customers, leads, invoices… or type a command"
        value={query}
        onValueChange={setQuery}
        data-testid="command-palette-input"
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {results.length > 0 && (
          <>
            <CommandGroup heading="Search results">
              {results.map((r) => {
                const Icon = TYPE_ICON[r.type] || Building2;
                return (
                  <CommandItem key={`${r.type}-${r.id}`} value={`${r.label} ${r.meta}`} onSelect={() => go(TYPE_ROUTE[r.type])}>
                    <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span className="flex-1">{r.label}</span>
                    <span className="text-xs capitalize text-muted-foreground">{r.type}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}
        <CommandGroup heading="Quick actions">
          <CommandItem value="create lead new" onSelect={() => go("/app/crm?new=1")}>
            <Plus className="mr-2 h-4 w-4 text-primary" /> Create lead
          </CommandItem>
          <CommandItem value="create invoice new" onSelect={() => go("/app/billing?new=1")}>
            <Plus className="mr-2 h-4 w-4 text-primary" /> Create invoice
          </CommandItem>
          <CommandItem value="create ticket new" onSelect={() => go("/app/support?new=1")}>
            <Plus className="mr-2 h-4 w-4 text-primary" /> Create ticket
          </CommandItem>
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Navigate">
          {NAV_ACTIONS.map((a) => (
            <CommandItem key={a.to} value={a.label} onSelect={() => go(a.to)}>
              <a.icon className="mr-2 h-4 w-4 text-muted-foreground" /> {a.label}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
